import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useTheme } from "../components/ThemeProvider";
import { dialogs } from "../context/DialogContext";
import usePermiso from "../hooks/usePermiso";
import api from "../services/api";
import TelonGestionVentas from "../components/gestionVentas/TelonGestionVentas";
import ResultadosBusquedaExpediente from "../components/gestionVentas/ResultadosBusquedaExpediente";
import ModalConfeccionContrato from "../components/gestionVentas/ModalConfeccionContrato";
import ModalReconocimientoHonorarios from "../components/gestionVentas/ModalReconocimientoHonorarios";
import MenuSelectorContratos from "../components/gestionVentas/MenuSelectorContratos";
import VisorDocumentosNasExpediente from "../components/gestionVentas/VisorDocumentosNasExpediente";
import ModalVistaPreviaDocumentoNas from "../components/gestionVentas/ModalVistaPreviaDocumentoNas";
import { prefetchNasPreview } from "../utils/nasPreviewCache";
import ModalInsertarSenal from "../components/gestionVentas/ModalInsertarSenal";
import ModalAnadirCompradorExpediente from "../components/gestionVentas/ModalAnadirCompradorExpediente";
import ModalAnadirPropiedadExpediente from "../components/gestionVentas/ModalAnadirPropiedadExpediente";
import ModalAnadirPropietarioExpediente from "../components/gestionVentas/ModalAnadirPropietarioExpediente";
import ModalWhatsAppMulti from "../components/gestionVentas/ModalWhatsAppMulti";
import ModalEdicionParteExpediente from "../components/gestionVentas/ModalEdicionParteExpediente";
import ModalEmail from "../components/ModalEmail";
import FichaResumenPropiedad from "../components/propiedades/FichaResumenPropiedad";
import { aplicarCompradorPipeline } from "../utils/aplicarCompradorPipeline";
import { cargarPropiedadExpediente } from "../utils/resolverPropiedadCompraCliente";
import { mergeFinanzasExpediente } from "../utils/finanzasExpedientePropiedad";
import {
  parsePrecioVentaEntero, parseEuroAmount, parsePercentFlexible, formatPercent2,
  formatPrecioVentaConEuro, formatEuroConSimbolo,
} from "../utils/moneyParse";
import {
  resolverContactosSeleccionados, contactoParaModal, llamarContacto,
} from "../utils/gestionVentasContactos";
import { EMPRESAS_EXPEDIENTE, aplicarIbansAgencia } from "../utils/empresaExpediente";
import {
  abrirCarpetaExpedienteEnWindows,
  NAS_HELPER_INSTALAR_MSG,
  normalizarRutaUnc,
  rutaCarpetaDesdeNumero,
} from "../utils/abrirRutaWindows";
import AddressAutocompleteInput from "../components/AddressAutocompleteInput";
import { calleLineaDesdePlaces } from "../utils/googlePlacesAddressLine";

const TIPOS = ["Contado", "Alquiler", "Gestión", "Financiación", "Autogestión"];
const ESTADOS = ["Pendiente", "En gestión", "Firmado"];
const CONCEPTOS_AGENTE = ["Venta", "Captación", "Colaboración", "Otro"];
const ENTREGA_TIPOS = [
  { v: "comprador", l: "Comprador" },
  { v: "agencia", l: "Agencia" },
  { v: "propietario", l: "Propietario" },
];

/** Mitad del padding lateral del formulario PropiedadForm (16px → 8px). */
const GV_PAD_X = 32;
/** Canal lateral reservado (~mitad del margen útil con maxWidth 1400 en pantalla estándar). */
const GV_GUTTER_SIDE = 176;
/** Altura fila tablas partes / señales (px). */
const GV_ROW_H = 26;
/** Filas visibles sin scroll en Compradores, Propiedad y Propietarios. */
const GV_PARTES_VISIBLE = 4;
const GV_PARTES_TABLE_H = GV_ROW_H * GV_PARTES_VISIBLE + 20;
/** Altura textarea observaciones (~4 líneas). */
const GV_OBS_TEXT_H = 84;
/** Altura mínima del área scroll de Señales (~4 filas de datos). */
const GV_SENALES_MIN_H = GV_ROW_H * 5 + 20;

const EXP_VACIO = () => ({
  fecha: new Date().toISOString().slice(0, 10),
  cliente_nombre: "",
  cliente_id: null,
  tipo: "Contado",
  estado: "Pendiente",
  empresa: "Vitalcasa",
  observaciones: "",
  gastos: "Comprador",
  escrituras_en_orden: false,
  pendiente_de: "",
  precio_venta: "",
  precio_propietario: "",
  comision_comprador: "",
  comision_comprador_iva: "",
  comision_propietario: "",
  comision_propietario_iva: "",
  honorarios: "",
  honorarios_iva: "",
  honorarios_colaborador: "",
  honorarios_colaborador_iva: "",
  honorarios_incluidos: false,
  precios_manual: false,
  nos_conocio_por: "",
  colaborador: "",
  plazo_contrato: "",
  fecha_fin_contrato: "",
  fecha_llamada: "",
  compradores: [],
  propiedades: [],
  propietarios: [],
  agentes: [],
  senales: [],
});

function calcularFechaFinContrato(fecha, plazoDias) {
  const dias = Number(plazoDias);
  if (!fecha || !Number.isFinite(dias) || dias <= 0) return "";
  const d = new Date(`${String(fecha).slice(0, 10)}T12:00:00`);
  if (Number.isNaN(d.getTime())) return "";
  d.setDate(d.getDate() + dias);
  return d.toISOString().slice(0, 10);
}

function fmtEuro(v) {
  if (v == null || v === "") return "";
  const n = typeof v === "number" ? v : (parseEuroAmount(v) ?? parsePrecioVentaEntero(v));
  if (!Number.isFinite(n)) return "";
  return formatEuroConSimbolo(n);
}

function fmtPrecioVenta(v) {
  if (v == null || v === "") return "";
  return formatPrecioVentaConEuro(v);
}

const CAMPOS_PRECIO_ENTERO = new Set(["precio_venta", "precio_propietario"]);

function numOrNull(parseFn, v) {
  const n = parseFn(v);
  return Number.isFinite(n) ? n : null;
}

/** Solo datos guardados en expediente; no mezclar con ficha cliente/contacto. */
function textoExpedienteParte(p) {
  return {
    nombre: String(p?.nombre ?? "").trim(),
    apellidos: String(p?.apellidos ?? "").trim(),
  };
}

function neto(base, iva) {
  const b = parseEuroAmount(base) ?? parsePrecioVentaEntero(base) ?? 0;
  const i = parseEuroAmount(iva) ?? 0;
  return (Number.isFinite(b) ? b : 0) + (Number.isFinite(i) ? i : 0);
}

export default function GestionVentas() {
  const { theme } = useTheme();
  const t = theme.colors;
  const isDark = theme.name === "dark";
  const tx = isDark ? "#ffffff" : t.textPrimary;

  // ── Permisos granulares ──
  const { puede } = usePermiso();
  const puedeVerNuevoExp = puede("gv_nuevo_expediente", "ver");
  const puedeVerBuscador = puede("gv_buscador", "ver");
  const puedeVerFiltroEstado = puede("gv_filtro_estado", "ver");
  const puedeVerEstadoPendiente = puede("gv_estado_pendiente", "ver");
  const puedeVerEstadoGestion = puede("gv_estado_gestion", "ver");
  const puedeVerEstadoFirmado = puede("gv_estado_firmado", "ver");
  const puedeVerFiltroTipo = puede("gv_filtro_tipo", "ver");
  const puedeVerTipoContado = puede("gv_tipo_contado", "ver");
  const puedeVerTipoAlquiler = puede("gv_tipo_alquiler", "ver");
  const puedeVerTipoGestion = puede("gv_tipo_gestion", "ver");
  const puedeVerTipoFinanciacion = puede("gv_tipo_financiacion", "ver");
  const puedeVerTipoAutogestion = puede("gv_tipo_autogestion", "ver");
  const puedeVerDatosComerciales = puede("gv_datos_comerciales", "ver");
  const puedeModificarDatosComerciales = puede("gv_datos_comerciales", "modificar");
  const puedeVerCompradores = puede("gv_compradores", "ver");
  const puedeModificarCompradores = puede("gv_compradores", "modificar");
  const puedeAltaCompradores = puede("gv_compradores", "alta");
  const puedeBajaCompradores = puede("gv_compradores", "baja");
  const puedeVerPropiedadesExp = puede("gv_propiedades_exp", "ver");
  const puedeModificarPropiedadesExp = puede("gv_propiedades_exp", "modificar");
  const puedeAltaPropiedadesExp = puede("gv_propiedades_exp", "alta");
  const puedeBajaPropiedadesExp = puede("gv_propiedades_exp", "baja");
  const puedeVerPropietariosExp = puede("gv_propietarios_exp", "ver");
  const puedeModificarPropietariosExp = puede("gv_propietarios_exp", "modificar");
  const puedeVerAgentesComisiones = puede("gv_agentes_comisiones", "ver");
  const puedeModificarAgentesComisiones = puede("gv_agentes_comisiones", "modificar");
  const puedeVerEntregaSenal = puede("gv_entrega_senal", "ver");
  const puedeModificarEntregaSenal = puede("gv_entrega_senal", "modificar");
  const puedeVerDocsNas = puede("gv_docs_nas", "ver");
  const puedeModificarDocsNas = puede("gv_docs_nas", "modificar");
  const puedeVerModalContrato = puede("gv_modal_contrato", "ver");
  const puedeModificarModalContrato = puede("gv_modal_contrato", "modificar");
  const puedeVerModalHonorarios = puede("gv_modal_honorarios", "ver");
  const puedeModificarModalHonorarios = puede("gv_modal_honorarios", "modificar");
  const puedeVerModalSenal = puede("gv_modal_senal", "ver");
  const puedeModificarModalSenal = puede("gv_modal_senal", "modificar");
  const puedeVerModalEstadoExpediente = puede("gv_modal_estado_expediente", "ver");
  const puedeModificarModalEstadoExpediente = puede("gv_modal_estado_expediente", "modificar");

  const [listaTodos, setListaTodos] = useState([]);
  const [resultadosBusqueda, setResultadosBusqueda] = useState([]);
  const [exp, setExp] = useState(null);
  const [form, setForm] = useState(EXP_VACIO());
  const formRef = useRef(form);
  useEffect(() => { formRef.current = form; }, [form]);
  const [esNuevo, setEsNuevo] = useState(false);
  const [modalAddComp, setModalAddComp] = useState(false);
  const [modalAddPropiedad, setModalAddPropiedad] = useState(false);
  const [modalAddPropiet, setModalAddPropiet] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [agentes, setAgentes] = useState([]);
  const [tab, setTab] = useState("comercial");
  const [filtroEstadoUi, setFiltroEstadoUi] = useState("");
  const [filtroTipoUi, setFiltroTipoUi] = useState("");
  const [resultadosEstado, setResultadosEstado] = useState([]);
  const [resultadosTipo, setResultadosTipo] = useState([]);
  const [buscandoEstado, setBuscandoEstado] = useState(false);
  const [buscandoTipo, setBuscandoTipo] = useState(false);
  const [panelEstado, setPanelEstado] = useState(false);
  const [panelTipo, setPanelTipo] = useState(false);
  const [buscarLista, setBuscarLista] = useState("");
  const [buscarQuery, setBuscarQuery] = useState("");
  const [buscandoBusqueda, setBuscandoBusqueda] = useState(false);
  const [selComp, setSelComp] = useState(() => new Set());
  const [selProp, setSelProp] = useState(() => new Set());
  const [modalContrato, setModalContrato] = useState(false);
  const [modalHonorarios, setModalHonorarios] = useState(false);
  const [menuContratos, setMenuContratos] = useState(false);
  const btnContratosRef = useRef(null);
  const [modalSenal, setModalSenal] = useState(false);
  const [modalWA, setModalWA] = useState(false);
  const [contactosWA, setContactosWA] = useState([]);
  const [modalEmail, setModalEmail] = useState(false);
  const [contactoAccion, setContactoAccion] = useState(null);
  const [modalParte, setModalParte] = useState(null);
  const [vistaPreviaPropId, setVistaPreviaPropId] = useState(null);
  const [previewDocNas, setPreviewDocNas] = useState(null);
  const [docsNasRefreshKey, setDocsNasRefreshKey] = useState(0);
  const [carpetaNas, setCarpetaNas] = useState("");
  const [docsNasSel, setDocsNasSel] = useState([]);
  const [emailDocsNas, setEmailDocsNas] = useState(null);
  const [estadoGuardado, setEstadoGuardado] = useState("");
  const expIdRef = useRef(null);
  const userModified = useRef(false);
  const skipAutoSave = useRef(false);
  const saveTimerRef = useRef(null);

  useEffect(() => { expIdRef.current = exp?.id ?? null; }, [exp?.id]);

  const GV = {
    compradores: isDark ? "#60a5fa" : "#2563eb",
    propiedad: isDark ? "#22d3ee" : "#0891b2",
    propietarios: isDark ? "#c4b5fd" : "#7c3aed",
    agentes: isDark ? "#fbbf24" : "#d97706",
    gastos: isDark ? "#4ade80" : "#059669",
    pendiente: isDark ? "#fb923c" : "#ea580c",
    documentos: isDark ? "#38bdf8" : "#0284c7",
    senales: isDark ? "#a5b4fc" : "#6366f1",
    obs: isDark ? "#94a3b8" : "#64748b",
  };

  const panelCol = (accent) => ({
    background: isDark ? `${accent}14` : `${accent}0c`,
    border: `1px solid ${isDark ? `${accent}55` : `${accent}40`}`,
    borderLeft: `3px solid ${accent}`,
    borderRadius: 6,
    padding: "5px 7px",
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
    overflow: "hidden",
    color: tx,
    height: "100%",
    boxSizing: "border-box",
    justifyContent: "flex-start",
  });
  const panelTitleCol = (accent) => ({
    fontSize: 11,
    fontWeight: 800,
    color: accent,
    letterSpacing: "0.07em",
    marginBottom: 4,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    paddingBottom: 3,
    borderBottom: `1px solid ${accent}33`,
  });
  const thCol = (accent) => ({
    ...th,
    color: accent,
    borderBottom: `1px solid ${accent}44`,
  });

  const inp = {
    padding: "2px 5px", borderRadius: 4, fontSize: 12, height: 23,
    background: isDark ? "#1a2030" : t.inputBg,
    border: `1px solid ${isDark ? "#5a6a80" : t.inputBorder}`,
    color: tx, outline: "none", fontFamily: "inherit",
    width: "100%", boxSizing: "border-box",
  };
  const inpCompact = { ...inp, height: 21, fontSize: 11, padding: "1px 4px" };
  const inpFin = { ...inpCompact, textAlign: "right" };
  const sel = { ...inp, cursor: "pointer" };
  const lbl = { fontSize: 11, fontWeight: 700, color: tx, textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 2 };
  const th = { fontSize: 10, fontWeight: 700, color: tx, textAlign: "left", padding: "2px 3px", borderBottom: `1px solid ${isDark ? "#4a5568" : t.border}`, whiteSpace: "nowrap" };
  const td = { padding: "1px 2px", verticalAlign: "middle", color: tx, height: 26 };
  const tdParte = { ...td, padding: "1px 1px", overflow: "hidden" };
  const scrollPartes = { overflowY: "auto", overflowX: "hidden", minHeight: GV_PARTES_TABLE_H, maxHeight: GV_PARTES_TABLE_H };
  const btnIcon = (color) => ({
    width: 18, height: 18, borderRadius: 4, border: "none", cursor: "pointer",
    background: color || t.accentBlue, color: "#fff", fontSize: 13, lineHeight: 1,
    display: "inline-flex", alignItems: "center", justifyContent: "center", padding: 0,
  });
  const btnSm = {
    padding: "3px 10px", borderRadius: 5, fontSize: 12, fontWeight: 700, cursor: "pointer",
    border: `1px solid ${isDark ? "#4a5568" : t.border}`,
    background: isDark ? "#1a2030" : t.bgCard,
    color: tx, fontFamily: "inherit",
  };
  const btnPrimary = { ...btnSm, background: t.accentBlue, color: "#fff", border: "none" };

  const cargarListaTodos = useCallback(async () => {
    try {
      const r = await api.get("/expedientes?limit=500");
      setListaTodos(r.data.data || []);
    } catch (e) { console.error(e); }
  }, []);

  const cargarBusqueda = useCallback(async () => {
    const q = buscarQuery.trim();
    if (!q) {
      setResultadosBusqueda([]);
      return;
    }
    setBuscandoBusqueda(true);
    try {
      const r = await api.get(`/expedientes?buscar=${encodeURIComponent(q)}&limit=500`);
      setResultadosBusqueda(r.data.data || []);
    } catch (e) {
      console.error(e);
      setResultadosBusqueda([]);
    } finally {
      setBuscandoBusqueda(false);
    }
  }, [buscarQuery]);

  const buscarPorEstado = async (estado) => {
    if (!estado) {
      setResultadosEstado([]);
      setPanelEstado(false);
      return;
    }
    setBuscandoEstado(true);
    setPanelEstado(true);
    setPanelTipo(false);
    setFiltroTipoUi("");
    try {
      const r = await api.get(`/expedientes?estado=${encodeURIComponent(estado)}`);
      setResultadosEstado(r.data.data || []);
    } catch (e) {
      console.error(e);
      setResultadosEstado([]);
    } finally {
      setBuscandoEstado(false);
    }
  };

  const buscarPorTipo = async (tipo) => {
    if (!tipo) {
      setResultadosTipo([]);
      setPanelTipo(false);
      return;
    }
    setBuscandoTipo(true);
    setPanelTipo(true);
    setPanelEstado(false);
    setFiltroEstadoUi("");
    try {
      const r = await api.get(`/expedientes?tipo=${encodeURIComponent(tipo)}`);
      setResultadosTipo(r.data.data || []);
    } catch (e) {
      console.error(e);
      setResultadosTipo([]);
    } finally {
      setBuscandoTipo(false);
    }
  };

  useEffect(() => { cargarListaTodos(); }, [cargarListaTodos]);

  useEffect(() => {
    const t = setTimeout(() => setBuscarQuery(buscarLista.trim()), 320);
    return () => clearTimeout(t);
  }, [buscarLista]);

  useEffect(() => {
    const t = setTimeout(() => { void cargarBusqueda(); }, 320);
    return () => clearTimeout(t);
  }, [cargarBusqueda]);

  useEffect(() => {
    const propId = form.propiedades[0]?.propiedad_id;
    if (!propId || form.precios_manual) return undefined;
    let alive = true;
    (async () => {
      try {
        const cargada = await cargarPropiedadExpediente({ propiedad_id: propId });
        if (!alive || !cargada) return;
        const fin = mergeFinanzasExpediente(form, cargada, { precios_manual: false });
        if (Object.keys(fin).length) setForm((prev) => ({ ...prev, ...fin }));
      } catch { /* ignore */ }
    })();
    return () => { alive = false; };
  }, [form.propiedades[0]?.propiedad_id, form.precios_manual]);

  useEffect(() => {
    api.get("/agentes").then((r) => setAgentes(r.data.data || r.data || [])).catch(() => {});
  }, []);
  const aplicarExpediente = (data) => {
    skipAutoSave.current = true;
    userModified.current = false;
    setExp(data);
    setEsNuevo(false);
    setCarpetaNas(data.carpeta_nas || "");
    setDocsNasSel([]);
    setSelComp(new Set());
    setSelProp(new Set());
    const ent = (v) => (v == null || v === "" ? "" : formatPrecioVentaConEuro(v));
    const eur = (v) => (v == null || v === "" ? "" : formatEuroConSimbolo(v));
    setForm({
      ...EXP_VACIO(),
      ...data,
      precios_manual: !!data.precios_manual,
      gastos: data.gastos === "Según Ley" ? "Comprador" : (data.gastos || "Comprador"),
      fecha: data.fecha ? String(data.fecha).slice(0, 10) : "",
      fecha_fin_contrato: data.fecha_fin_contrato ? String(data.fecha_fin_contrato).slice(0, 10) : "",
      plazo_contrato: data.plazo_contrato != null && data.plazo_contrato !== "" ? String(data.plazo_contrato) : "",
      fecha_llamada: data.fecha_llamada ? String(data.fecha_llamada).slice(0, 10) : "",
      precio_venta: ent(data.precio_venta),
      precio_propietario: ent(data.precio_propietario),
      comision_comprador: eur(data.comision_comprador),
      comision_comprador_iva: eur(data.comision_comprador_iva),
      comision_propietario: eur(data.comision_propietario),
      comision_propietario_iva: eur(data.comision_propietario_iva),
      honorarios: eur(data.honorarios),
      honorarios_iva: eur(data.honorarios_iva),
      honorarios_colaborador: eur(data.honorarios_colaborador),
      honorarios_colaborador_iva: eur(data.honorarios_colaborador_iva),
      compradores: (data.compradores || []).map((c) => ({
        ...c,
        ...textoExpedienteParte(c),
      porcentaje: (v => (v == null || v === "" ? "" : String(numOrNull(parsePercentFlexible, v) ?? v)))(c.porcentaje),
      tipo_documento: c.tipo_documento || "DNI",
      dni: c.dni || "",
    })),
      propiedades: data.propiedades || [],
      propietarios: (data.propietarios || []).map((p) => ({
        ...p,
        ...textoExpedienteParte(p),
      tipo_documento: p.tipo_documento || "DNI",
      dni: p.dni || "",
      nuda_propiedad: (v => (v == null || v === "" ? "" : String(numOrNull(parsePercentFlexible, v) ?? v)))(p.nuda_propiedad),
        pleno_dominio: (v => (v == null || v === "" ? "" : String(numOrNull(parsePercentFlexible, v) ?? v)))(p.pleno_dominio),
        usufructo: (v => (v == null || v === "" ? "" : String(numOrNull(parsePercentFlexible, v) ?? v)))(p.usufructo),
      })),
      agentes: data.agentes || [],
      senales: (data.senales || []).map((s) => ({
        ...s,
        fecha: s.fecha ? String(s.fecha).slice(0, 10) : "",
        medio: s.medio || "",
        importe_comprador: eur(s.importe_comprador),
        importe_agencia: eur(s.importe_agencia),
        importe_propietario: eur(s.importe_propietario),
      })),
    });
    queueMicrotask(() => { skipAutoSave.current = false; });
  };

  const abrirExpedientePorId = async (id) => {
    if (!id) return;
    setBuscarLista("");
    setBuscarQuery("");
    setFiltroEstadoUi("");
    setFiltroTipoUi("");
    setPanelEstado(false);
    setPanelTipo(false);
    setResultadosEstado([]);
    setResultadosTipo([]);
    try {
      const r = await api.get(`/expedientes/${id}`);
      aplicarExpediente(r.data.data);
    } catch (e) {
      await dialogs.alert("Error al abrir expediente: " + (e.response?.data?.error || e.message));
    }
  };

  const onBuscarKeyDown = (e) => {
    if (e.key !== "Enter" || !buscarQuery || buscandoBusqueda) return;
    if (resultadosBusqueda.length === 1) {
      e.preventDefault();
      abrirExpedientePorId(resultadosBusqueda[0].id);
    }
  };

  const vaciarExpediente = () => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    userModified.current = false;
    skipAutoSave.current = true;
    setExp(null);
    setEsNuevo(false);
    setForm(EXP_VACIO());
    setCarpetaNas("");
    setDocsNasSel([]);
    setEstadoGuardado("");
    setSelComp(new Set());
    setSelProp(new Set());
    queueMicrotask(() => { skipAutoSave.current = false; });
  };

  const nuevoExpediente = () => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    userModified.current = false;
    skipAutoSave.current = true;
    setExp(null);
    setEsNuevo(true);
    setForm(EXP_VACIO());
    setCarpetaNas("");
    setDocsNasSel([]);
    setEstadoGuardado("");
    setSelComp(new Set());
    setSelProp(new Set());
    queueMicrotask(() => { skipAutoSave.current = false; });
  };

  const setF = (k, v) => {
    if (!skipAutoSave.current) userModified.current = true;
    setForm((prev) => ({ ...prev, [k]: v }));
  };

  const marcarPreciosManual = () => {
    setForm((prev) => (prev.precios_manual ? prev : { ...prev, precios_manual: true }));
  };

  const cambiarCampoMoneda = (k, v) => {
    marcarPreciosManual();
    setF(k, v);
  };

  const blurCampoMoneda = (k, v) => {
    setF(k, CAMPOS_PRECIO_ENTERO.has(k) ? formatPrecioVentaConEuro(v) : formatEuroConSimbolo(v));
  };

  const patchSenales = (fn) => {
    if (!skipAutoSave.current) userModified.current = true;
    setForm((prev) => ({ ...prev, senales: fn(prev.senales || []) }));
  };

  const blurSenalImporte = (i, campo, v) => {
    patchSenales((arr) => {
      const next = [...arr];
      next[i] = { ...next[i], [campo]: formatEuroConSimbolo(v) };
      return next;
    });
  };

  const toggleSelComp = (i) => {
    setSelComp((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i); else next.add(i);
      return next;
    });
  };
  const toggleSelProp = (i) => {
    setSelProp((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i); else next.add(i);
      return next;
    });
  };

  const obtenerContactosSeleccionados = async () => {
    if (!selComp.size && !selProp.size) {
      await dialogs.alert("Selecciona al menos un comprador o propietario.");
      return null;
    }
    return resolverContactosSeleccionados(form.compradores, form.propietarios, selComp, selProp);
  };

  const resolverContactosDocs = (compIdx, propIdx) =>
    resolverContactosSeleccionados(form.compradores, form.propietarios, compIdx, propIdx);

  const accionLlamar = async () => {
    const contactos = await obtenerContactosSeleccionados();
    if (!contactos?.length) return;
    const conTel = contactos.filter((c) => c.telefono);
    if (!conTel.length) {
      await dialogs.alert("Ningún contacto seleccionado tiene teléfono.");
      return;
    }
    try {
      await llamarContacto(conTel[0], { clienteId: form.cliente_id, leadId: conTel[0].lead_id });
      await dialogs.alert("Llamada iniciada — tu móvil recibirá la llamada en breve.", { autoCloseMs: 2000 });
    } catch (e) {
      await dialogs.alert(e.message || "Error al llamar");
    }
  };

  const accionWhatsApp = async () => {
    const contactos = await obtenerContactosSeleccionados();
    if (!contactos?.length) return;
    const conTel = contactos.filter((c) => c.telefono).map((c) => contactoParaModal(c));
    if (!conTel.length) {
      await dialogs.alert("Ningún contacto seleccionado tiene teléfono.");
      return;
    }
    setContactosWA(conTel);
    setModalWA(true);
  };

  const accionEmail = async () => {
    const contactos = await obtenerContactosSeleccionados();
    if (!contactos?.length) return;
    if (contactos.length > 1) {
      await dialogs.alert("Selecciona un solo contacto para email.");
      return;
    }
    if (!contactos[0].email) {
      await dialogs.alert("El contacto seleccionado no tiene email.");
      return;
    }
    setEmailDocsNas(null);
    setContactoAccion(contactos[0]);
    setModalEmail(true);
  };

  const abrirWADocumentos = async (contactos, docs) => {
    const conTel = contactos.filter((c) => c.telefono).map((c) => contactoParaModal(c));
    if (!conTel.length) {
      await dialogs.alert("Ningún contacto del destinatario tiene teléfono.");
      return;
    }
    setDocsNasSel(docs);
    setContactosWA(conTel);
    setModalWA(true);
  };

  const abrirEmailDocumentos = async (contactos, docs) => {
    if (!exp?.id) {
      await dialogs.alert("Guarda el expediente antes de enviar documentos.");
      return;
    }
    if (contactos.length > 1) {
      await dialogs.alert("Hay varios emails posibles. Se abrirá el modal para el primero con email válido.");
    }
    setDocsNasSel(docs);
    setEmailDocsNas({
      expedienteId: exp.id,
      archivos: docs,
      numeroExpediente: exp.numero || "",
    });
    setContactoAccion(contactos[0]);
    setModalEmail(true);
  };

  const insertarSenal = (senal) => {
    patchSenales((arr) => [...arr, {
      ...senal,
      importe_comprador: senal.importe_comprador ? formatEuroConSimbolo(senal.importe_comprador) : "",
      importe_agencia: senal.importe_agencia ? formatEuroConSimbolo(senal.importe_agencia) : "",
      importe_propietario: senal.importe_propietario ? formatEuroConSimbolo(senal.importe_propietario) : "",
    }]);
  };

  const patchSenal = (i, patch) => {
    patchSenales((arr) => {
      const next = [...arr];
      let merged = { ...next[i], ...patch };
      if ("entrega_tipo" in patch || "recibe_tipo" in patch) {
        merged = { ...merged, ...aplicarIbansAgencia({ ...merged, empresa: formRef.current.empresa }) };
      }
      next[i] = merged;
      return next;
    });
  };

  const cambiarEmpresa = (empresa) => {
    if (!skipAutoSave.current) userModified.current = true;
    setForm((prev) => ({
      ...prev,
      empresa,
      senales: prev.senales.map((s) => ({
        ...s,
        ...aplicarIbansAgencia({
          entrega_tipo: s.entrega_tipo,
          recibe_tipo: s.recibe_tipo,
          iban_salida: s.iban_salida,
          iban_entrada: s.iban_entrada,
          empresa,
        }),
      })),
    }));
  };

  const linkTxt = (accent) => ({
    color: accent,
    cursor: "pointer",
    textDecoration: "underline",
    fontWeight: 600,
    fontSize: 12,
    background: "none",
    border: "none",
    padding: 0,
    fontFamily: "inherit",
    textAlign: "left",
    display: "block",
    width: "100%",
    maxWidth: "100%",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  });

  const abrirParteExpediente = (tipo, parte, index) => {
    setModalParte({ tipo, index, parte: { ...parte } });
  };

  const abrirVistaPreviaPropiedad = async (p) => {
    if (p.propiedad_id) {
      setVistaPreviaPropId(p.propiedad_id);
      return;
    }
    const ref = String(p.referencia || "").trim();
    if (!ref) return;
    try {
      const r = await api.get(`/propiedades?buscar=${encodeURIComponent(ref)}&perPage=1&cartera=todas`);
      const lista = r.data?.data || r.data?.propiedades || [];
      if (lista[0]?.id) setVistaPreviaPropId(lista[0].id);
      else await dialogs.alert("No se encontró la propiedad con esa referencia.");
    } catch {
      await dialogs.alert("Error al buscar la propiedad.");
    }
  };

  const abrirCarpetaNas = async () => {
    const ruta = normalizarRutaUnc(
      carpetaNas || exp?.carpeta_nas || rutaCarpetaDesdeNumero(exp?.numero),
    );
    if (!ruta) {
      await dialogs.alert("No hay número de expediente para calcular la carpeta NAS.");
      return;
    }
    const result = await abrirCarpetaExpedienteEnWindows(ruta);
    if (result.ok) return;
    if (result.needsInstall) {
      await dialogs.alert(
        "Access abre carpetas porque es un programa de Windows. El CRM en el navegador necesita un conector local (solo una vez por PC):\n\n" +
        NAS_HELPER_INSTALAR_MSG +
        "\n\nTras instalarlo, el botón 📁 abrirá el Explorador igual que en Access.",
        { autoCloseMs: 12000 },
      );
      return;
    }
    if (result.clipboardOk) {
      await dialogs.alert(
        "La ruta está en el portapapeles. Pulsa Win+E, pega en la barra de dirección y Enter.",
        { autoCloseMs: 4000 },
      );
    }
  };

  const confirmarAnadirComprador = async (payload) => {
    if (payload.tipo === "pipeline") {
      try {
        const patch = await aplicarCompradorPipeline(payload.lead, form, payload.comprador);
        if (!skipAutoSave.current) userModified.current = true;
        setForm((prev) => ({ ...prev, ...patch }));
      } catch (e) {
        await dialogs.alert("Error al cargar datos del pipeline: " + (e.message || e));
      }
      return;
    }
    setF("compradores", [...form.compradores, payload.comprador]);
  };

  const confirmarAnadirPropiedad = ({ propiedad, extra }) => {
    if (!skipAutoSave.current) userModified.current = true;
    setForm((prev) => {
      const next = { ...prev, propiedades: [...prev.propiedades, propiedad] };
      if (!prev.propietarios.length && extra?.propietarios?.length) next.propietarios = extra.propietarios;
      Object.assign(next, mergeFinanzasExpediente(prev, extra, { precios_manual: prev.precios_manual }));
      return next;
    });
  };

  const confirmarAnadirPropietario = (propietario) => {
    setF("propietarios", [...form.propietarios, propietario]);
  };

  const filasPartes = Math.max(form.compradores.length, form.propiedades.length, form.propietarios.length);
  const fechaFinMostrada = form.plazo_contrato
    ? calcularFechaFinContrato(form.fecha, form.plazo_contrato)
    : (form.fecha_fin_contrato || "");

  const payloadFromForm = (src = form) => ({
    fecha: src.fecha,
    cliente_nombre: src.cliente_nombre,
    cliente_id: src.cliente_id || null,
    tipo: src.tipo,
    estado: src.estado,
    empresa: src.empresa,
    observaciones: src.observaciones,
    gastos: src.gastos,
    escrituras_en_orden: src.escrituras_en_orden,
    pendiente_de: src.pendiente_de,
    honorarios_incluidos: src.honorarios_incluidos,
    precios_manual: !!src.precios_manual,
    nos_conocio_por: src.nos_conocio_por,
    colaborador: src.colaborador,
    plazo_contrato: src.plazo_contrato ? Number(src.plazo_contrato) || null : null,
    precio_venta: numOrNull(parsePrecioVentaEntero, src.precio_venta),
    precio_propietario: numOrNull(parsePrecioVentaEntero, src.precio_propietario),
    comision_comprador: numOrNull(parseEuroAmount, src.comision_comprador),
    comision_comprador_iva: numOrNull(parseEuroAmount, src.comision_comprador_iva),
    comision_propietario: numOrNull(parseEuroAmount, src.comision_propietario),
    comision_propietario_iva: numOrNull(parseEuroAmount, src.comision_propietario_iva),
    honorarios: numOrNull(parseEuroAmount, src.honorarios),
    honorarios_iva: numOrNull(parseEuroAmount, src.honorarios_iva),
    honorarios_colaborador: numOrNull(parseEuroAmount, src.honorarios_colaborador),
    honorarios_colaborador_iva: numOrNull(parseEuroAmount, src.honorarios_colaborador_iva),
    fecha_fin_contrato: calcularFechaFinContrato(src.fecha, src.plazo_contrato) || src.fecha_fin_contrato || null,
    fecha_llamada: src.fecha_llamada || null,
    compradores: src.compradores.map((c, i) => ({
      lead_id: c.lead_id || null,
      cliente_id: c.cliente_id || null,
      ...textoExpedienteParte(c),
      porcentaje: numOrNull(parsePercentFlexible, c.porcentaje) ?? 100,
      tipo_documento: c.tipo_documento || "DNI",
      dni: c.dni || "",
      direccion: c.direccion || "",
      poblacion: c.poblacion || "",
      provincia: c.provincia || "",
      codigo_postal: c.codigo_postal || "",
      estado_civil: c.estado_civil || "",
      regimen_matrimonial: c.regimen_matrimonial || "",
      profesion: c.profesion || "",
      sexo: c.sexo || "",
      nacionalidad: c.nacionalidad || "",
      orden: i,
    })),
    propiedades: src.propiedades.map((p, i) => ({
      propiedad_id: p.propiedad_id || null,
      referencia: p.referencia || p.prop_ref || "",
      direccion: p.direccion || p.prop_dir || "",
      poblacion: p.poblacion || "",
      provincia: p.provincia || "",
      vpo: !!p.vpo,
      orden: i,
    })),
    propietarios: src.propietarios.map((p, i) => ({
      contacto_id: p.contacto_id || null,
      ...textoExpedienteParte(p),
      tipo_documento: p.tipo_documento || "DNI",
      dni: p.dni || "",
      direccion: p.direccion || "",
      poblacion: p.poblacion || "",
      provincia: p.provincia || "",
      codigo_postal: p.codigo_postal || "",
      estado_civil: p.estado_civil || "",
      regimen_matrimonial: p.regimen_matrimonial || "",
      profesion: p.profesion || "",
      sexo: p.sexo || "",
      nacionalidad: p.nacionalidad || "",
      nuda_propiedad: numOrNull(parsePercentFlexible, p.nuda_propiedad),
      pleno_dominio: numOrNull(parsePercentFlexible, p.pleno_dominio),
      usufructo: numOrNull(parsePercentFlexible, p.usufructo),
      orden: i,
    })),
    agentes: src.agentes.map((a, i) => ({
      agente_id: a.agente_id || null,
      concepto: a.concepto || "Venta",
      orden: i,
    })),
    senales: (src.senales || []).map((s, i) => ({
      fecha: s.fecha || null,
      concepto: s.concepto || "",
      iban_salida: s.iban_salida || "",
      iban_entrada: s.iban_entrada || "",
      medio: s.medio || "",
      entrega_tipo: s.entrega_tipo || "",
      entrega_id: s.entrega_id || null,
      recibe_tipo: s.recibe_tipo || "",
      recibe_id: s.recibe_id || null,
      importe_comprador: numOrNull(parseEuroAmount, s.importe_comprador) ?? 0,
      importe_agencia: numOrNull(parseEuroAmount, s.importe_agencia) ?? 0,
      importe_propietario: numOrNull(parseEuroAmount, s.importe_propietario) ?? 0,
      tiene_scan: !!s.tiene_scan,
      orden: i,
    })),
  });

  const persistirPartesExpediente = async (nextForm) => {
    if (!exp?.id) return;
    try {
      const body = payloadFromForm(nextForm);
      await api.patch(`/expedientes/${exp.id}`, {
        compradores: body.compradores,
        propietarios: body.propietarios,
      });
    } catch (e) {
      console.error("[GV] guardar parte:", e);
    }
  };

  const guardarParteExpediente = (index, tipo, datos) => {
    const key = tipo === "comprador" ? "compradores" : "propietarios";
    if (!skipAutoSave.current) userModified.current = true;
    setForm((prev) => {
      const arr = [...prev[key]];
      arr[index] = { ...arr[index], ...datos };
      const next = { ...prev, [key]: arr };
      if (exp?.id) void persistirPartesExpediente(next);
      return next;
    });
  };

  const expedienteTieneContenido = (body) => !!(
    String(body.cliente_nombre || "").trim()
    || body.compradores?.length
    || body.propiedades?.length
  );

  const guardarSilencioso = useCallback(async () => {
    const src = formRef.current;
    const body = payloadFromForm(src);
    if (!expedienteTieneContenido(body) && !expIdRef.current) return;

    setGuardando(true);
    setEstadoGuardado("guardando");
    try {
      if (!expIdRef.current) {
        const r = await api.post("/expedientes", body);
        aplicarExpediente(r.data.data);
        await cargarListaTodos();
      } else {
        const r = await api.patch(`/expedientes/${expIdRef.current}`, body);
        const data = r.data?.data;
        if (data?.carpeta_nas) {
          setCarpetaNas(data.carpeta_nas);
          setExp((prev) => (prev ? { ...prev, carpeta_nas: data.carpeta_nas, numero: data.numero ?? prev.numero } : prev));
        }
      }
      setEstadoGuardado("guardado");
      setTimeout(() => setEstadoGuardado(""), 2500);
    } catch (e) {
      setEstadoGuardado("error");
      console.error("[GV autoguardado]", e);
    } finally {
      setGuardando(false);
    }
  }, [cargarListaTodos]);

  const guardarAhora = useCallback(async () => {
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }
    if (!userModified.current) return;
    userModified.current = false;
    await guardarSilencioso();
  }, [guardarSilencioso]);

  const abrirMenuContratos = async () => {
    await guardarAhora();
    if (!expIdRef.current) {
      await dialogs.alert("Añade datos al expediente (comprador o cliente) y espera unos segundos antes de generar contratos.");
      return;
    }
    setMenuContratos((v) => !v);
  };

  const abrirModalContratoArras = () => {
    setModalContrato(true);
  };

  const abrirModalHonorarios = () => {
    setModalHonorarios(true);
  };

  const honorariosNetoExpediente = useMemo(() => {
    const total = neto(form.honorarios, form.honorarios_iva);
    return total > 0 ? formatEuroConSimbolo(total) : "";
  }, [form.honorarios, form.honorarios_iva]);

  useEffect(() => {
    if (!userModified.current || skipAutoSave.current) return;
    if (!exp?.id && !esNuevo) return;

    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      userModified.current = false;
      void guardarSilencioso();
    }, 800);

    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, [form, esNuevo, exp?.id, guardarSilencioso]);

  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState !== "hidden") return;
      if (!userModified.current) return;
      void guardarAhora();
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [guardarAhora]);

  /** Datos visibles en pantalla para el Word (aunque no se haya guardado el expediente). */
  const datosContratoPantalla = () => {
    const f = formRef.current;
    const p = payloadFromForm(f);
    return {
      usar_datos_pantalla: true,
      compradores: p.compradores,
      propietarios: p.propietarios,
      propiedades: p.propiedades.map((prop, i) => ({
        ...prop,
        tipo_inmueble_nombre: f.propiedades[i]?.tipo_inmueble_nombre || "",
      })),
      senales: p.senales,
      precio_venta: p.precio_venta,
      fecha: p.fecha,
      empresa: p.empresa,
      honorarios: p.honorarios,
      honorarios_iva: p.honorarios_iva,
    };
  };

  const avisarDocumentoGeneradoNas = async ({ nombre, rutaNas, carpetaGen, carpetaNas: carpetaGen2, guardadoNas, errorNas, needsReinstall, docsSync, carpetaNasActualizada }) => {
    const carpetaGenFinal = carpetaGen || carpetaGen2;
    if (carpetaNasActualizada) setCarpetaNas(carpetaNasActualizada);
    else if (carpetaGenFinal) setCarpetaNas(carpetaGenFinal);
    if (guardadoNas) {
      setDocsNasRefreshKey((k) => k + 1);
      if (nombre && expIdRef.current) {
        void prefetchNasPreview(expIdRef.current, nombre);
        setPreviewDocNas(nombre);
      }
      return;
    }
    const detalle = errorNas && errorNas !== "guardado_en_pc_requiere_helper" ? `\n\nDetalle: ${errorNas}` : "";
    const reinstall = needsReinstall
      ? "\n\nEl conector NAS está desactualizado en este PC.\n\n" + NAS_HELPER_INSTALAR_MSG
      : "\n\n" + NAS_HELPER_INSTALAR_MSG;
    await dialogs.alert(
      `No se pudo guardar en el NAS. El archivo se ha descargado en Descargas: ${nombre}.` +
      detalle +
      reinstall +
      `\n\nCarpeta prevista:\n${carpetaGenFinal || carpetaNas || form.carpeta_nas || "—"}`,
      { autoCloseMs: 15000 },
    );
  };

  const eliminarExp = async () => {
    if (!exp?.id) return;
    const ok = await dialogs.confirm("¿Eliminar este expediente?");
    if (!ok) return;
    await api.delete(`/expedientes/${exp.id}`);
    vaciarExpediente();
    await cargarListaTodos();
  };

  const opcionesExpediente = useMemo(() => {
    if (!exp?.id || listaTodos.some((e) => e.id === exp.id)) return listaTodos;
    const p0 = form.propiedades?.[0];
    return [{
      id: exp.id,
      numero: exp.numero,
      cliente_nombre: form.cliente_nombre,
      resumen_propiedad: p0?.direccion || p0?.referencia || "",
    }, ...listaTodos];
  }, [listaTodos, exp?.id, exp?.numero, form.cliente_nombre, form.propiedades]);

  const idxActual = exp ? listaTodos.findIndex((e) => e.id === exp.id) : -1;
  const irAnterior = () => { if (idxActual > 0) api.get(`/expedientes/${listaTodos[idxActual - 1].id}`).then((r) => aplicarExpediente(r.data.data)); };
  const irSiguiente = () => { if (idxActual >= 0 && idxActual < listaTodos.length - 1) api.get(`/expedientes/${listaTodos[idxActual + 1].id}`).then((r) => aplicarExpediente(r.data.data)); };

  const totSenales = form.senales.reduce(
    (acc, s) => ({
      comprador: acc.comprador + (parseEuroAmount(s.importe_comprador) || 0),
      agencia: acc.agencia + (parseEuroAmount(s.importe_agencia) || 0),
      propietario: acc.propietario + (parseEuroAmount(s.importe_propietario) || 0),
    }),
    { comprador: 0, agencia: 0, propietario: 0 }
  );

  const tabsFuturas = ["Preparación Firma", "Post-venta", "Financiación", "Historial"];
  const expedienteVisible = !!exp?.id || esNuevo;

  const quickRepliesExpediente = useMemo(() => {
    const p = form.propiedades?.[0];
    return {
      poblacion: p?.poblacion || "",
      direccion: p?.direccion || "",
      archivos: docsNasSel,
    };
  }, [form.propiedades, docsNasSel]);

  return (
    <div className="gv-page" style={{
      height: "calc(100vh - 52px)", display: "grid",
      gridTemplateColumns: `${GV_GUTTER_SIDE}px minmax(0, 1fr) ${GV_GUTTER_SIDE}px`,
      background: t.bgApp, boxSizing: "border-box", overflow: "hidden",
      fontFamily: "'Segoe UI', system-ui, sans-serif", color: tx,
    }}>
      <div aria-hidden="true" data-gv-gutter="left" />
      <div style={{
        minWidth: 0, height: "100%", display: "flex", flexDirection: "column",
        padding: `6px ${GV_PAD_X}px`, boxSizing: "border-box", overflow: "hidden",
      }}>
      {isDark && (
        <style>{`
          .gv-page input::placeholder,
          .gv-page textarea::placeholder { color: #d1d5db !important; opacity: 1; }
          .gv-page select option { background: #1a2030; color: #ffffff; }
          .gv-page input[type="date"] { color-scheme: dark; }
        `}</style>
      )}
      {/* ── Cabecera expediente ── */}
      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 14, flexShrink: 0, position: "relative", zIndex: 20 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <button style={btnSm} onClick={irAnterior} disabled={idxActual <= 0} title="Anterior">◀</button>
          <select style={{ ...sel, width: 90, fontWeight: 700 }} value={exp?.id || ""} onChange={(e) => {
            const id = e.target.value;
            if (!id) { vaciarExpediente(); return; }
            api.get(`/expedientes/${id}`).then((r) => aplicarExpediente(r.data.data));
          }}>
            <option value="">--</option>
            {opcionesExpediente.map((e) => {
              const extra = [e.cliente_nombre, e.resumen_propiedad].filter(Boolean).join(" · ");
              const label = extra ? `${e.numero} — ${extra}` : e.numero;
              return <option key={e.id} value={e.id}>{label}</option>;
            })}
          </select>
          <button style={btnSm} onClick={irSiguiente} disabled={idxActual < 0 || idxActual >= listaTodos.length - 1} title="Siguiente">▶</button>
          {puedeVerNuevoExp && (
            <button style={{ ...btnSm, fontWeight: 800 }} onClick={nuevoExpediente} title="Nuevo expediente">+</button>
          )}
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <span style={lbl}>Fecha</span>
          <input type="date" style={{ ...inp, width: 118 }} value={form.fecha} onChange={(e) => setF("fecha", e.target.value)} />
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 4, flex: "0 1 200px", maxWidth: 200 }}>
          <span style={lbl}>Cliente</span>
          <input style={{ ...inp, width: "100%" }} value={form.cliente_nombre} onChange={(e) => setF("cliente_nombre", e.target.value)} placeholder="Nombre del expediente" />
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <span style={lbl}>Tipo</span>
          <select style={{ ...sel, width: 110 }} value={form.tipo} onChange={(e) => setF("tipo", e.target.value)}>
            {TIPOS.map((x) => <option key={x} value={x}>{x}</option>)}
          </select>
        </div>

        {puedeVerModalEstadoExpediente && (
          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <span style={lbl}>Estado</span>
            <select style={{ ...sel, width: 100 }} value={form.estado} onChange={(e) => setF("estado", e.target.value)} disabled={!puedeModificarModalEstadoExpediente}>
              {ESTADOS.map((x) => <option key={x} value={x}>{x}</option>)}
            </select>
          </div>
        )}

        {puedeVerBuscador && (
          <div style={{ position: "relative", flex: "1 1 120px", minWidth: 108, marginLeft: "auto" }}>
            <input
              style={{ ...inp, width: "100%" }}
              placeholder="Buscar..."
              value={buscarLista}
              onChange={(e) => setBuscarLista(e.target.value)}
              onKeyDown={onBuscarKeyDown}
            />
            <ResultadosBusquedaExpediente
              buscar={buscarQuery}
              resultados={resultadosBusqueda}
              buscando={buscandoBusqueda}
              onAbrir={abrirExpedientePorId}
              abierto={!!buscarQuery.trim()}
            />
          </div>
        )}

        <div style={{ display: "flex", alignItems: "center", gap: 4, flexShrink: 0, marginLeft: puedeVerBuscador ? 0 : "auto" }}>
          {puedeVerFiltroEstado && (
            <div style={{ position: "relative" }}>
              <select
                style={{ ...sel, width: 100 }}
                value={filtroEstadoUi}
                onChange={(e) => {
                  const v = e.target.value;
                  setFiltroEstadoUi(v);
                  buscarPorEstado(v);
                }}
              >
                <option value="">Estado</option>
                {puedeVerEstadoPendiente && <option value="Pendiente">Pendiente</option>}
                {puedeVerEstadoGestion && <option value="En gestión">En gestión</option>}
                {puedeVerEstadoFirmado && <option value="Firmado">Firmado</option>}
              </select>
              <ResultadosBusquedaExpediente
                abierto={panelEstado && !!filtroEstadoUi}
                etiqueta={`${resultadosEstado.length} expediente(s) — Estado: ${filtroEstadoUi}`}
                resultados={resultadosEstado}
                buscando={buscandoEstado}
                onAbrir={abrirExpedientePorId}
                alinearDerecha
              />
            </div>
          )}
          {puedeVerFiltroTipo && (
            <div style={{ position: "relative" }}>
              <select
                style={{ ...sel, width: 100 }}
                value={filtroTipoUi}
                onChange={(e) => {
                  const v = e.target.value;
                  setFiltroTipoUi(v);
                  buscarPorTipo(v);
                }}
              >
                <option value="">Tipo</option>
                {puedeVerTipoContado && <option value="Contado">Contado</option>}
                {puedeVerTipoAlquiler && <option value="Alquiler">Alquiler</option>}
                {puedeVerTipoGestion && <option value="Gestión">Gestión</option>}
                {puedeVerTipoFinanciacion && <option value="Financiación">Financiación</option>}
                {puedeVerTipoAutogestion && <option value="Autogestión">Autogestión</option>}
              </select>
              <ResultadosBusquedaExpediente
                abierto={panelTipo && !!filtroTipoUi}
                etiqueta={`${resultadosTipo.length} expediente(s) — Tipo: ${filtroTipoUi}`}
                resultados={resultadosTipo}
                buscando={buscandoTipo}
                onAbrir={abrirExpedientePorId}
                alinearDerecha
              />
            </div>
          )}
        </div>
      </div>

      <div style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
      {!expedienteVisible ? (
        <TelonGestionVentas />
      ) : (
      <>
      {/* ── Pestañas ── */}
      <div style={{ display: "flex", gap: 2, marginBottom: 14, flexShrink: 0 }}>
        {puedeVerDatosComerciales && (
          <button style={{ ...btnSm, ...(tab === "comercial" ? { background: t.accentBlue, color: "#fff", border: "none" } : {}) }}
            onClick={() => setTab("comercial")}>Datos comerciales</button>
        )}
        {tabsFuturas.map((tb) => (
          <button key={tb} style={{ ...btnSm, opacity: 0.55, cursor: "not-allowed", color: tx }} disabled title="Próximamente">{tb}</button>
        ))}
        <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 4 }}>
          <span style={lbl}>Empresa</span>
          <select style={{ ...sel, width: 118 }} value={form.empresa} onChange={(e) => cambiarEmpresa(e.target.value)} disabled={!puedeModificarDatosComerciales}>
            {EMPRESAS_EXPEDIENTE.map((x) => <option key={x} value={x}>{x}</option>)}
          </select>
        </div>
      </div>

      {tab === "comercial" && puedeVerDatosComerciales && (
        <div style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
        <>
        <div style={{ display: "grid", gridTemplateColumns: "1fr auto 1fr", gap: 6, marginBottom: 14, flexShrink: 0, alignItems: "center" }}>
          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            <span style={{ ...lbl, marginBottom: 0 }}>Acciones</span>
            <button style={btnSm} onClick={accionLlamar} title="Llamar al contacto seleccionado" disabled={!puedeModificarDatosComerciales}>📞 Llamar</button>
            <button style={{ ...btnSm, color: "#16a34a" }} onClick={accionWhatsApp} title="WhatsApp" disabled={!puedeModificarDatosComerciales}>WhatsApp</button>
            <button style={btnSm} onClick={accionEmail} title="Email" disabled={!puedeModificarDatosComerciales}>✉ Email</button>
            {(puedeVerModalContrato || puedeVerModalHonorarios) && (
              <button
                ref={btnContratosRef}
                style={btnSm}
                onClick={abrirMenuContratos}
                title="Generar contratos del expediente"
                disabled={!exp?.id}
              >
                Contratos ▾
              </button>
            )}
            <MenuSelectorContratos
              isOpen={menuContratos}
              anchorRef={btnContratosRef}
              onClose={() => setMenuContratos(false)}
              onContratoArras={abrirModalContratoArras}
              onReconocimientoHonorarios={abrirModalHonorarios}
              mostrarContratoArras={puedeVerModalContrato}
              mostrarReconocimientoHonorarios={puedeVerModalHonorarios}
              isDark={isDark}
              tx={tx}
            />
          </div>
          <div style={{ display: "flex", gap: 4, justifyContent: "center" }}>
            <button
              style={{ ...btnSm, fontSize: 16, padding: "4px 12px", lineHeight: 1 }}
              onClick={abrirCarpetaNas}
              title="Abrir carpeta NAS del expediente"
              disabled={!puedeModificarDatosComerciales}
            >
              📁
            </button>
          </div>
          <div style={{ textAlign: "right", fontSize: 11, color: tx, opacity: 0.75 }}>
            {(selComp.size > 0 || selProp.size > 0) ? `${selComp.size + selProp.size} seleccionado(s)` : "\u00A0"}
          </div>
        </div>
        <div style={{ flex: 1, display: "grid", gridTemplateRows: "auto auto 1fr auto", gap: 6, minHeight: 0 }}>
          {/* Fila 1: Compradores | Propiedad | Propietarios — 4 filas visibles, scroll desde la 5.ª */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1.08fr 1.12fr", gap: 6, minHeight: GV_PARTES_TABLE_H + 36, alignItems: "stretch" }}>
            {/* Compradores */}
            {puedeVerCompradores && (
            <div style={{ ...panelCol(GV.compradores), overflow: "hidden" }}>
              <div style={panelTitleCol(GV.compradores)}>
                <span>COMPRADORES</span>
                {puedeAltaCompradores && puede("gv_modal_nuevo_contacto", "ver") && (
                  <button style={btnIcon(GV.compradores)} title="Añadir comprador" onClick={() => setModalAddComp(true)}>+</button>
                )}
              </div>
              <div style={scrollPartes}>
              <table style={{ width: "calc(100% - 2px)", borderCollapse: "collapse", tableLayout: "fixed" }}>
                <colgroup>
                  <col style={{ width: 22 }} />
                  <col style={{ width: 98 }} />
                  <col style={{ width: "auto" }} />
                  <col style={{ width: 62 }} />
                  <col style={{ width: 18 }} />
                </colgroup>
                <thead><tr>
                  <th style={{ ...thCol(GV.compradores), width: 22 }}></th>
                  <th style={thCol(GV.compradores)}>Nombre</th><th style={thCol(GV.compradores)}>Apellidos</th><th style={{ ...thCol(GV.compradores), width: 62 }}>%</th><th style={{ ...thCol(GV.compradores), width: 18 }}></th>
                </tr></thead>
                <tbody>
                  {Array.from({ length: filasPartes || 0 }).map((_, i) => {
                    const c = form.compradores[i];
                    if (!c) return <tr key={`ec-${i}`} aria-hidden="true"><td style={td} colSpan={5} /></tr>;
                    return (
                      <tr key={i}>
                        <td style={td}>
                          <input type="checkbox" checked={selComp.has(i)} onChange={() => toggleSelComp(i)} title="Seleccionar comprador" />
                        </td>
                        <td style={td}>
                          {puedeModificarCompradores ? (
                            <button type="button" style={linkTxt(GV.compradores)} onClick={() => abrirParteExpediente("comprador", c, i)} title="Editar comprador">
                              {c.nombre || "—"}
                            </button>
                          ) : (
                            <span style={linkTxt(GV.compradores)}>{c.nombre || "—"}</span>
                          )}
                        </td>
                        <td style={tdParte}>
                          {puedeModificarCompradores ? (
                            <button type="button" style={linkTxt(GV.compradores)} onClick={() => abrirParteExpediente("comprador", c, i)} title="Editar comprador">
                              {c.apellidos || "—"}
                            </button>
                          ) : (
                            <span style={linkTxt(GV.compradores)}>{c.apellidos || "—"}</span>
                          )}
                        </td>
                        <td style={td}>
                          <input
                            style={{ ...inpCompact, textAlign: "right" }}
                            value={c.porcentaje ?? ""}
                            onChange={(e) => { const arr = [...form.compradores]; arr[i] = { ...arr[i], porcentaje: e.target.value }; setF("compradores", arr); }}
                            onBlur={(e) => { const arr = [...form.compradores]; arr[i] = { ...arr[i], porcentaje: formatPercent2(e.target.value) }; setF("compradores", arr); }}
                            placeholder="100,00"
                            disabled={!puedeModificarCompradores}
                          />
                        </td>
                        <td style={td}>{puedeBajaCompradores && (
                          <button style={btnIcon("#ef4444")} onClick={() => setF("compradores", form.compradores.filter((_, j) => j !== i))}>×</button>
                        )}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              </div>
            </div>
            )}

            {/* Propiedad */}
            {puedeVerPropiedadesExp && (
            <div style={panelCol(GV.propiedad)}>
              <div style={panelTitleCol(GV.propiedad)}>
                <span>PROPIEDAD</span>
                {puedeAltaPropiedadesExp && (
                  <button style={btnIcon(GV.propiedad)} title="Añadir propiedad" onClick={() => setModalAddPropiedad(true)}>+</button>
                )}
              </div>
              <div style={scrollPartes}>
              <table style={{ width: "calc(100% - 2px)", borderCollapse: "collapse", tableLayout: "fixed" }}>
                <colgroup>
                  <col style={{ width: 58 }} />
                  <col style={{ width: "auto" }} />
                  <col style={{ width: 54 }} />
                  <col style={{ width: 18 }} />
                </colgroup>
                <thead><tr>
                  <th style={{ ...thCol(GV.propiedad), width: 58 }}>Ref.</th><th style={thCol(GV.propiedad)}>Dirección</th><th style={{ ...thCol(GV.propiedad), width: 54 }}>Pobl.</th><th style={{ ...thCol(GV.propiedad), width: 18 }}></th>
                </tr></thead>
                <tbody>
                  {Array.from({ length: filasPartes || 0 }).map((_, i) => {
                    const p = form.propiedades[i];
                    if (!p) return <tr key={`ep-${i}`} aria-hidden="true"><td style={td} colSpan={4} /></tr>;
                    return (
                      <tr key={i}>
                        <td style={td}>
                          {String(p.referencia || "").trim() ? (
                            <button type="button" style={linkTxt(GV.propiedad)} onClick={() => abrirVistaPreviaPropiedad(p)} title="Vista previa propiedad">
                              {p.referencia}
                            </button>
                          ) : (
                            <input style={inpCompact} value={p.referencia || ""} onChange={(e) => { const arr = [...form.propiedades]; arr[i] = { ...arr[i], referencia: e.target.value }; setF("propiedades", arr); }} disabled={!puedeModificarPropiedadesExp} />
                          )}
                        </td>
                        <td style={tdParte}>
                          <AddressAutocompleteInput
                            value={p.direccion || ""}
                            onValueChange={(v) => {
                              const arr = [...form.propiedades];
                              arr[i] = { ...arr[i], direccion: v };
                              setF("propiedades", arr);
                            }}
                            onPlaceAddressResolved={(parts) => {
                              const arr = [...form.propiedades];
                              arr[i] = {
                                ...arr[i],
                                direccion: calleLineaDesdePlaces(parts) || arr[i].direccion,
                                poblacion: parts.poblacion || arr[i].poblacion,
                                provincia: parts.provincia || arr[i].provincia,
                              };
                              setF("propiedades", arr);
                            }}
                            style={inpCompact}
                            disabled={!puedeModificarPropiedadesExp}
                          />
                        </td>
                        <td style={td}><input style={inpCompact} value={p.poblacion || ""} onChange={(e) => { const arr = [...form.propiedades]; arr[i] = { ...arr[i], poblacion: e.target.value }; setF("propiedades", arr); }} disabled={!puedeModificarPropiedadesExp} /></td>
                        <td style={td}>{puedeBajaPropiedadesExp && (
                          <button style={btnIcon("#ef4444")} onClick={() => setF("propiedades", form.propiedades.filter((_, j) => j !== i))}>×</button>
                        )}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              </div>
            </div>
            )}

            {/* Propietarios */}
            {puedeVerPropietariosExp && (
            <div style={panelCol(GV.propietarios)}>
              <div style={panelTitleCol(GV.propietarios)}>
                <span>PROPIETARIOS</span>
                {puedeModificarPropietariosExp && (
                  <button style={btnIcon(GV.propietarios)} title="Añadir propietario" onClick={() => setModalAddPropiet(true)}>+</button>
                )}
              </div>
              <div style={scrollPartes}>
              <table style={{ width: "calc(100% - 2px)", borderCollapse: "collapse", tableLayout: "fixed" }}>
                <colgroup>
                  <col style={{ width: 22 }} />
                  <col style={{ width: 92 }} />
                  <col />
                  <col style={{ width: 58 }} />
                  <col style={{ width: 58 }} />
                  <col style={{ width: 58 }} />
                  <col style={{ width: 18 }} />
                </colgroup>
                <thead><tr>
                  <th style={{ ...thCol(GV.propietarios), width: 22 }}></th>
                  <th style={{ ...thCol(GV.propietarios), width: 92 }}>Nombre</th><th style={{ ...thCol(GV.propietarios) }}>Apellidos</th>
                  <th style={{ ...thCol(GV.propietarios), width: 58 }}>Nuda</th><th style={{ ...thCol(GV.propietarios), width: 58 }}>Pleno</th><th style={{ ...thCol(GV.propietarios), width: 58 }}>Usuf.</th><th style={{ ...thCol(GV.propietarios), width: 18 }}></th>
                </tr></thead>
                <tbody>
                  {Array.from({ length: filasPartes || 0 }).map((_, i) => {
                    const p = form.propietarios[i];
                    if (!p) return <tr key={`epr-${i}`} aria-hidden="true"><td style={td} colSpan={7} /></tr>;
                    return (
                      <tr key={i}>
                        <td style={td}>
                          <input type="checkbox" checked={selProp.has(i)} onChange={() => toggleSelProp(i)} title="Seleccionar propietario" />
                        </td>
                        <td style={td}>
                          {puedeModificarPropietariosExp ? (
                            <button type="button" style={linkTxt(GV.propietarios)} onClick={() => abrirParteExpediente("propietario", p, i)} title="Editar propietario">
                              {p.nombre || "—"}
                            </button>
                          ) : (
                            <span style={linkTxt(GV.propietarios)}>{p.nombre || "—"}</span>
                          )}
                        </td>
                        <td style={tdParte}>
                          {puedeModificarPropietariosExp ? (
                            <button type="button" style={linkTxt(GV.propietarios)} onClick={() => abrirParteExpediente("propietario", p, i)} title="Editar propietario">
                              {p.apellidos || "—"}
                            </button>
                          ) : (
                            <span style={linkTxt(GV.propietarios)}>{p.apellidos || "—"}</span>
                          )}
                        </td>
                        <td style={td}><input style={{ ...inpCompact, textAlign: "right" }} value={p.nuda_propiedad ?? ""} onChange={(e) => { const arr = [...form.propietarios]; arr[i] = { ...arr[i], nuda_propiedad: e.target.value }; setF("propietarios", arr); }} disabled={!puedeModificarPropietariosExp} /></td>
                        <td style={td}><input style={{ ...inpCompact, textAlign: "right" }} value={p.pleno_dominio ?? ""} onChange={(e) => { const arr = [...form.propietarios]; arr[i] = { ...arr[i], pleno_dominio: e.target.value }; setF("propietarios", arr); }} disabled={!puedeModificarPropietariosExp} /></td>
                        <td style={td}><input style={{ ...inpCompact, textAlign: "right" }} value={p.usufructo ?? ""} onChange={(e) => { const arr = [...form.propietarios]; arr[i] = { ...arr[i], usufructo: e.target.value }; setF("propietarios", arr); }} disabled={!puedeModificarPropietariosExp} /></td>
                        <td style={td}>{puedeModificarPropietariosExp && (
                          <button style={btnIcon("#ef4444")} onClick={() => setF("propietarios", form.propietarios.filter((_, j) => j !== i))}>×</button>
                        )}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              </div>
            </div>
            )}
          </div>

          {/* Fila 2: Agentes | Precios | Gastos | Documentos */}
          <div style={{ display: "grid", gridTemplateColumns: "290px 340px minmax(100px, 0.72fr) minmax(160px, 1fr)", gap: 6, alignItems: "stretch" }}>
            {puedeVerAgentesComisiones && (
            <div style={{ ...panelCol(GV.agentes), alignSelf: "stretch", overflow: "hidden" }}>
              <div style={panelTitleCol(GV.agentes)}>
                <span>AGENTES</span>
                {puedeModificarAgentesComisiones && (
                  <button style={btnIcon(GV.agentes)} onClick={() => setF("agentes", [...form.agentes, { concepto: "Venta", agente_id: "" }])}>+</button>
                )}
              </div>
              <div style={{ flex: 1, minHeight: 0, overflowY: "auto", display: "flex", flexDirection: "column", gap: 4 }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead><tr><th style={thCol(GV.agentes)}>Concepto</th><th style={thCol(GV.agentes)}>Nombre</th><th style={{ ...thCol(GV.agentes), width: 18 }}></th></tr></thead>
                <tbody>
                  {form.agentes.map((a, i) => (
                    <tr key={i}>
                      <td style={td}>
                        <select style={sel} value={a.concepto || "Venta"} onChange={(e) => { const arr = [...form.agentes]; arr[i] = { ...arr[i], concepto: e.target.value }; setF("agentes", arr); }} disabled={!puedeModificarAgentesComisiones}>
                          {CONCEPTOS_AGENTE.map((x) => <option key={x} value={x}>{x}</option>)}
                        </select>
                      </td>
                      <td style={td}>
                        <select style={sel} value={a.agente_id || ""} onChange={(e) => { const arr = [...form.agentes]; arr[i] = { ...arr[i], agente_id: e.target.value || null }; setF("agentes", arr); }} disabled={!puedeModificarAgentesComisiones}>
                          <option value="">—</option>
                          {agentes.map((ag) => <option key={ag.id} value={ag.id}>{ag.nombre}</option>)}
                        </select>
                      </td>
                      <td style={td}>{puedeModificarAgentesComisiones && (
                        <button style={btnIcon("#ef4444")} onClick={() => setF("agentes", form.agentes.filter((_, j) => j !== i))}>×</button>
                      )}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 4, marginTop: 4 }}>
                <div><div style={lbl}>Nos conoció por</div><input style={inp} value={form.nos_conocio_por} onChange={(e) => setF("nos_conocio_por", e.target.value)} disabled={!puedeModificarAgentesComisiones} /></div>
                <div><div style={lbl}>Colaborador</div><input style={inp} value={form.colaborador} onChange={(e) => setF("colaborador", e.target.value)} disabled={!puedeModificarAgentesComisiones} /></div>
                <div style={{ gridColumn: "1 / -1" }}>
                  <div style={lbl}>Fin contrato</div>
                  <input type="date" style={{ ...inp, opacity: fechaFinMostrada ? 1 : 0.65 }} value={fechaFinMostrada} readOnly title="Calculado desde la fecha del expediente y el plazo del contrato" />
                </div>
              </div>
              </div>
            </div>
            )}

            {/* Precios */}
            <div style={{ ...panelCol(GV.gastos), width: 352, maxWidth: "100%", alignSelf: "stretch" }}>
              <div style={{ ...panelTitleCol(GV.gastos), marginBottom: 6 }}>
                <span>PRECIOS</span>
                <label style={{ fontSize: 10, display: "flex", alignItems: "center", gap: 4, fontWeight: 600, color: tx }}>
                  <input type="checkbox" checked={form.honorarios_incluidos} onChange={(e) => setF("honorarios_incluidos", e.target.checked)} disabled={!puedeModificarDatosComerciales} />
                  Hon. incl.
                </label>
              </div>
              <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed", fontSize: 11 }}>
                <colgroup>
                  <col style={{ width: 82 }} /><col /><col style={{ width: 80 }} /><col style={{ width: 72 }} />
                </colgroup>
                <thead><tr>
                  <th style={thCol(GV.gastos)}></th><th style={{ ...thCol(GV.gastos), textAlign: "right" }}>Base</th>
                  <th style={{ ...thCol(GV.gastos), textAlign: "right" }}>IVA</th><th style={{ ...thCol(GV.gastos), textAlign: "right" }}>Neto</th>
                </tr></thead>
                <tbody>
                  {[
                    { label: "Pr. Venta", k: "precio_venta", iva: null },
                    { label: "Pr. Propietario", k: "precio_propietario", iva: null },
                    { label: "Com. Compr.", k: "comision_comprador", iva: "comision_comprador_iva" },
                    { label: "Com. Prop.", k: "comision_propietario", iva: "comision_propietario_iva" },
                    { label: "Honorarios", k: "honorarios", iva: "honorarios_iva" },
                    { label: "Hon. colab.", k: "honorarios_colaborador", iva: "honorarios_colaborador_iva" },
                  ].map((row) => (
                    <tr key={row.k} style={{ height: 28 }}>
                      <td style={{ ...td, fontWeight: 600, fontSize: 10, whiteSpace: "nowrap", padding: "3px 2px" }}>{row.label}</td>
                      <td style={{ ...td, padding: "3px 1px" }}>
                        <input
                          style={inpFin}
                          value={form[row.k]}
                          onChange={(e) => cambiarCampoMoneda(row.k, e.target.value)}
                          onBlur={(e) => blurCampoMoneda(row.k, e.target.value)}
                          disabled={!puedeModificarDatosComerciales}
                        />
                      </td>
                      <td style={{ ...td, padding: "3px 1px" }}>
                        {row.iva ? (
                          <input
                            style={inpFin}
                            value={form[row.iva]}
                            onChange={(e) => cambiarCampoMoneda(row.iva, e.target.value)}
                            onBlur={(e) => blurCampoMoneda(row.iva, e.target.value)}
                            disabled={!puedeModificarDatosComerciales}
                          />
                        ) : <span style={{ color: tx, fontSize: 10 }}>—</span>}
                      </td>
                      <td style={{ ...td, textAlign: "right", fontWeight: 700, fontSize: 10, paddingRight: 4 }}>
                        {row.iva ? fmtEuro(neto(form[row.k], form[row.iva])) : fmtPrecioVenta(form[row.k])}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Gastos */}
            <div style={{ ...panelCol(GV.pendiente), alignSelf: "stretch", minWidth: 0, display: "flex", flexDirection: "column" }}>
              <div style={panelTitleCol(GV.pendiente)}><span>GASTOS</span></div>
              <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 4, flexWrap: "wrap" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                  <span style={{ ...lbl, marginBottom: 0, color: GV.pendiente }}>Gastos</span>
                  <select style={{ ...sel, width: 92, height: 21, fontSize: 11 }} value={form.gastos} onChange={(e) => setF("gastos", e.target.value)} disabled={!puedeModificarDatosComerciales}>
                    <option value="Según Ley">Según Ley</option>
                    <option value="Comprador">Comprador</option>
                    <option value="Vendedor">Vendedor</option>
                    <option value="Mitad">Mitad</option>
                  </select>
                </div>
                <label style={{ fontSize: 10, display: "flex", alignItems: "center", gap: 2, color: tx }}>
                  <input type="checkbox" checked={form.escrituras_en_orden} onChange={(e) => setF("escrituras_en_orden", e.target.checked)} disabled={!puedeModificarDatosComerciales} /> Escrituras en orden
                </label>
              </div>
              <div style={{ ...lbl, color: GV.pendiente }}>Pendiente de</div>
              <textarea
                style={{ ...inpCompact, flex: 1, resize: "none", minHeight: 0, lineHeight: 1.35, width: "100%" }}
                placeholder="Documentación, trámite o gestión pendiente..."
                value={form.pendiente_de}
                onChange={(e) => setF("pendiente_de", e.target.value)}
                disabled={!puedeModificarDatosComerciales}
              />
            </div>

            {puedeVerDocsNas && (
            <VisorDocumentosNasExpediente
              expedienteId={exp?.id}
              numeroExpediente={exp?.numero || ""}
              compradores={form.compradores}
              propietarios={form.propietarios}
              resolverContactos={resolverContactosDocs}
              onSelDocsChange={setDocsNasSel}
              onAbrirWhatsApp={abrirWADocumentos}
              onAbrirEmail={abrirEmailDocumentos}
              archivosRefreshKey={docsNasRefreshKey}
              accent={GV.documentos}
              tx={tx}
              isDark={isDark}
              btnSm={btnSm}
              thCol={thCol}
              td={td}
              inpCompact={inpCompact}
              panelCol={panelCol}
              panelTitleCol={panelTitleCol}
              puedeModificar={puedeModificarDocsNas}
            />
            )}
          </div>

          {/* Señales — ocupa el espacio vertical libre */}
          {puedeVerEntregaSenal && (
          <div style={{ ...panelCol(GV.senales), height: "100%", minHeight: GV_SENALES_MIN_H, display: "flex", flexDirection: "column", overflow: "hidden" }}>
            <div style={panelTitleCol(GV.senales)}>
              <span>SEÑALES</span>
              {puedeVerModalSenal && (
                <button style={btnIcon(GV.senales)} title="Insertar señal" onClick={() => setModalSenal(true)}>+</button>
              )}
            </div>
            <div style={{ flex: 1, minHeight: 0, overflow: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 860, tableLayout: "fixed" }}>
                <colgroup>
                  <col style={{ width: 84 }} />
                  <col style={{ width: 88 }} />
                  <col style={{ width: 68 }} />
                  <col style={{ width: 68 }} />
                  <col style={{ width: 104 }} />
                  <col style={{ width: 104 }} />
                  <col style={{ width: 72 }} />
                  <col style={{ width: 62 }} />
                  <col style={{ width: 62 }} />
                  <col style={{ width: 62 }} />
                  <col style={{ width: 26 }} />
                  <col style={{ width: 22 }} />
                </colgroup>
                <thead><tr>
                  <th style={thCol(GV.senales)}>Fecha</th>
                  <th style={thCol(GV.senales)}>Concepto</th>
                  <th style={thCol(GV.senales)}>Entrega</th>
                  <th style={thCol(GV.senales)}>Recibe</th>
                  <th style={thCol(GV.senales)}>IBAN salida</th>
                  <th style={thCol(GV.senales)}>IBAN entrada</th>
                  <th style={thCol(GV.senales)}>Medio</th>
                  <th style={{ ...thCol(GV.senales), textAlign: "right" }}>Compr.</th>
                  <th style={{ ...thCol(GV.senales), textAlign: "right" }}>Agenc.</th>
                  <th style={{ ...thCol(GV.senales), textAlign: "right" }}>Prop.</th>
                  <th style={thCol(GV.senales)}>Scan</th>
                  <th style={thCol(GV.senales)}></th>
                </tr></thead>
                <tbody>
                  {form.senales.map((s, i) => (
                    <tr key={i}>
                      <td style={td}><input type="date" style={inpCompact} value={s.fecha || ""} onChange={(e) => patchSenal(i, { fecha: e.target.value })} disabled={!puedeModificarEntregaSenal} /></td>
                      <td style={td}><input style={inpCompact} value={s.concepto || ""} onChange={(e) => patchSenal(i, { concepto: e.target.value })} disabled={!puedeModificarEntregaSenal} /></td>
                      <td style={td}>
                        <select style={{ ...sel, ...inpCompact, padding: "0 2px" }} value={s.entrega_tipo || ""} onChange={(e) => patchSenal(i, { entrega_tipo: e.target.value })} disabled={!puedeModificarEntregaSenal}>
                          <option value="">—</option>
                          {ENTREGA_TIPOS.map((x) => <option key={x.v} value={x.v}>{x.l}</option>)}
                        </select>
                      </td>
                      <td style={td}>
                        <select style={{ ...sel, ...inpCompact, padding: "0 2px" }} value={s.recibe_tipo || ""} onChange={(e) => patchSenal(i, { recibe_tipo: e.target.value })} disabled={!puedeModificarEntregaSenal}>
                          <option value="">—</option>
                          {ENTREGA_TIPOS.map((x) => <option key={x.v} value={x.v}>{x.l}</option>)}
                        </select>
                      </td>
                      <td style={td}><input style={inpCompact} value={s.iban_salida || ""} onChange={(e) => patchSenales((arr) => { const n = [...arr]; n[i] = { ...n[i], iban_salida: e.target.value }; return n; })} disabled={!puedeModificarEntregaSenal} /></td>
                      <td style={td}><input style={inpCompact} value={s.iban_entrada || ""} onChange={(e) => patchSenales((arr) => { const n = [...arr]; n[i] = { ...n[i], iban_entrada: e.target.value }; return n; })} disabled={!puedeModificarEntregaSenal} /></td>
                      <td style={td}><input style={inpCompact} value={s.medio || ""} onChange={(e) => patchSenal(i, { medio: e.target.value })} disabled={!puedeModificarEntregaSenal} /></td>
                      <td style={td}>
                        <input
                          style={{ ...inpCompact, textAlign: "right" }}
                          value={s.importe_comprador ?? ""}
                          onChange={(e) => patchSenales((arr) => { const n = [...arr]; n[i] = { ...n[i], importe_comprador: e.target.value }; return n; })}
                          onBlur={(e) => blurSenalImporte(i, "importe_comprador", e.target.value)}
                          disabled={!puedeModificarEntregaSenal}
                        />
                      </td>
                      <td style={td}>
                        <input
                          style={{ ...inpCompact, textAlign: "right" }}
                          value={s.importe_agencia ?? ""}
                          onChange={(e) => patchSenales((arr) => { const n = [...arr]; n[i] = { ...n[i], importe_agencia: e.target.value }; return n; })}
                          onBlur={(e) => blurSenalImporte(i, "importe_agencia", e.target.value)}
                          disabled={!puedeModificarEntregaSenal}
                        />
                      </td>
                      <td style={td}>
                        <input
                          style={{ ...inpCompact, textAlign: "right" }}
                          value={s.importe_propietario ?? ""}
                          onChange={(e) => patchSenales((arr) => { const n = [...arr]; n[i] = { ...n[i], importe_propietario: e.target.value }; return n; })}
                          onBlur={(e) => blurSenalImporte(i, "importe_propietario", e.target.value)}
                          disabled={!puedeModificarEntregaSenal}
                        />
                      </td>
                      <td style={td}><input type="checkbox" checked={!!s.tiene_scan} onChange={(e) => patchSenal(i, { tiene_scan: e.target.checked })} disabled={!puedeModificarEntregaSenal} /></td>
                      <td style={td}>{puedeModificarEntregaSenal && (
                        <button style={btnIcon("#ef4444")} onClick={() => patchSenales((arr) => arr.filter((_, j) => j !== i))}>×</button>
                      )}</td>
                    </tr>
                  ))}
                  {form.senales.length > 0 && (
                    <tr style={{ background: isDark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.03)" }}>
                      <td colSpan={7} style={{ ...td, fontWeight: 800, fontSize: 11, textAlign: "right" }}>TOTALES</td>
                      <td style={{ ...td, textAlign: "right", fontWeight: 800, fontSize: 11 }}>{fmtEuro(totSenales.comprador)}</td>
                      <td style={{ ...td, textAlign: "right", fontWeight: 800, fontSize: 11 }}>{fmtEuro(totSenales.agencia)}</td>
                      <td style={{ ...td, textAlign: "right", fontWeight: 800, fontSize: 11 }}>{fmtEuro(totSenales.propietario)}</td>
                      <td colSpan={2} />
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
          )}

          {/* Observaciones */}
          <div style={{ ...panelCol(GV.obs), flexShrink: 0, minHeight: GV_OBS_TEXT_H + 28, maxHeight: GV_OBS_TEXT_H + 32, height: "auto" }}>
            <div style={panelTitleCol(GV.obs)}><span>OBSERVACIONES</span></div>
            <textarea style={{ ...inpCompact, flex: 1, resize: "none", height: GV_OBS_TEXT_H, lineHeight: 1.25 }}
              value={form.observaciones} onChange={(e) => setF("observaciones", e.target.value)} disabled={!puedeModificarDatosComerciales} />
          </div>
        </div>
        </>
        <ModalInsertarSenal
          isOpen={modalSenal}
          onClose={() => setModalSenal(false)}
          onConfirm={insertarSenal}
          empresa={form.empresa}
          puedeModificar={puedeModificarModalSenal}
        />
        <ModalConfeccionContrato
          isOpen={modalContrato}
          onClose={() => setModalContrato(false)}
          expedienteId={exp?.id}
          empresa={form.empresa}
          getDatosPantalla={datosContratoPantalla}
          carpetaNasFallback={carpetaNas || rutaCarpetaDesdeNumero(exp?.numero)}
          numeroExpediente={exp?.numero || ""}
          onGenerado={async (payload) => {
            if (payload.plazo_contrato != null) {
              setForm((prev) => ({
                ...prev,
                plazo_contrato: String(payload.plazo_contrato),
                fecha_fin_contrato: payload.fecha_fin_contrato
                  ? String(payload.fecha_fin_contrato).slice(0, 10)
                  : prev.fecha_fin_contrato,
              }));
            }
            await avisarDocumentoGeneradoNas(payload);
          }}
          puedeModificar={puedeModificarModalContrato}
        />
        <ModalReconocimientoHonorarios
          isOpen={modalHonorarios}
          onClose={() => setModalHonorarios(false)}
          expedienteId={exp?.id}
          empresa={form.empresa}
          getDatosPantalla={datosContratoPantalla}
          importeHonorariosDefault={honorariosNetoExpediente}
          carpetaNasFallback={carpetaNas || rutaCarpetaDesdeNumero(exp?.numero)}
          numeroExpediente={exp?.numero || ""}
          onGenerado={avisarDocumentoGeneradoNas}
          puedeModificar={puedeModificarModalHonorarios}
        />
        {modalWA && (
          <ModalWhatsAppMulti
            isOpen
            contactos={contactosWA}
            crmContext={{ cliente_id: form.cliente_id, lead_id: form.compradores.find((c) => c.lead_id)?.lead_id }}
            documentosNas={
              docsNasSel.length && exp?.id
                ? { expedienteId: exp.id, archivos: docsNasSel }
                : null
            }
            quickRepliesExpediente={quickRepliesExpediente}
            onClose={() => { setModalWA(false); setContactosWA([]); }}
          />
        )}
        {modalEmail && contactoAccion && (
          <ModalEmail
            isOpen
            contact={contactoParaModal(contactoAccion)}
            crmContext={{
              cliente_id: contactoAccion.cliente_id || form.cliente_id,
              lead_id: contactoAccion.lead_id || form.compradores.find((c) => c.lead_id)?.lead_id,
            }}
            documentosNas={emailDocsNas}
            quickRepliesExpediente={quickRepliesExpediente}
            onClose={() => { setModalEmail(false); setContactoAccion(null); setEmailDocsNas(null); }}
            onSend={() => { setModalEmail(false); setContactoAccion(null); setEmailDocsNas(null); }}
          />
        )}
        {modalParte && (
          <ModalEdicionParteExpediente
            isOpen
            tipo={modalParte.tipo}
            parte={modalParte.parte}
            ubicacionExpediente={form.propiedades?.[0]}
            onClose={() => setModalParte(null)}
            onGuardado={(datos) => guardarParteExpediente(modalParte.index, modalParte.tipo, datos)}
          />
        )}
        {modalAddComp && (
          <ModalAnadirCompradorExpediente
            isOpen
            compradoresActuales={form.compradores}
            onClose={() => setModalAddComp(false)}
            onConfirm={confirmarAnadirComprador}
          />
        )}
        {modalAddPropiedad && (
          <ModalAnadirPropiedadExpediente
            isOpen
            propiedadesActuales={form.propiedades}
            onClose={() => setModalAddPropiedad(false)}
            onConfirm={confirmarAnadirPropiedad}
          />
        )}
        {modalAddPropiet && (
          <ModalAnadirPropietarioExpediente
            isOpen
            propietariosActuales={form.propietarios}
            onClose={() => setModalAddPropiet(false)}
            onConfirm={confirmarAnadirPropietario}
          />
        )}
        {vistaPreviaPropId && (
          <FichaResumenPropiedad propiedadId={vistaPreviaPropId} onClose={() => setVistaPreviaPropId(null)} />
        )}
        <ModalVistaPreviaDocumentoNas
          isOpen={!!previewDocNas}
          onClose={() => setPreviewDocNas(null)}
          expedienteId={exp?.id}
          nombreArchivo={previewDocNas}
          isDark={isDark}
          tx={tx}
        />
        </div>
      )}
      </>
      )}
      </div>
      </div>
      <div aria-hidden="true" data-gv-gutter="right" />
    </div>
  );
}
